import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/pt')) i18n.changeLanguage('pt');
    else if (path.startsWith('/de')) i18n.changeLanguage('de');
    else i18n.changeLanguage('en');
  }, [location.pathname, i18n]);

  const langCode = i18n.language || 'en';

  const content: Record<string, { title: string; h1: string; desc: string; sections: Array<{ h2: string; content: string }> }> = {
    en: {
      title: 'About Us | CSSBuy Spreadsheet Independent Resource',
      h1: 'About CSSBuy Spreadsheet',
      desc: 'Learn about our independent CSSBuy Spreadsheet resource hub. We provide organized product research guides, categories, and buying resources.',
      sections: [
        { h2: 'Our Mission', content: 'CSSBuy Spreadsheet is an independent resource hub dedicated to helping buyers organize their product research. We believe that structured information leads to better buying decisions. Our goal is to provide clear, categorized, and accessible resources that simplify the research process for anyone interested in buying products through agents.' },
        { h2: 'Independent Resource', content: 'This website is not affiliated with, endorsed by, or operated by CSSBuy. We are an independent third-party resource that provides informational content only. We do not process orders, handle payments, or manage any transactions. All information provided is for research and educational purposes.' },
        { h2: 'What We Provide', content: 'Our resource hub includes organized product categories, buying guides, shipping cost information, quality control tips, and general research tools. We strive to keep our content updated and relevant, helping users navigate the buying process with more confidence and better information.' },
        { h2: 'Contact', content: 'For questions or suggestions about our content, please reach out through our contact channels. We value feedback and aim to continuously improve our resource hub for the community.' },
      ],
    },
    pt: {
      title: 'Sobre Nós | CSSBuy Spreadsheet Recurso Independente',
      h1: 'Sobre o CSSBuy Spreadsheet',
      desc: 'Conheça nosso hub de recursos independente CSSBuy Spreadsheet. Fornecemos guias de pesquisa de produtos organizados, categorias e recursos de compra.',
      sections: [
        { h2: 'Nossa Missão', content: 'CSSBuy Spreadsheet é um hub de recursos independente dedicado a ajudar compradores a organizar sua pesquisa de produtos. Acreditamos que informações estruturadas levam a melhores decisões de compra. Nosso objetivo é fornecer recursos claros, categorizados e acessíveis que simplifiquem o processo de pesquisa para qualquer pessoa interessada em comprar produtos através de agentes.' },
        { h2: 'Recurso Independente', content: 'Este site não é afiliado, endossado ou operado pelo CSSBuy. Somos um recurso independente de terceiros que fornece apenas conteúdo informativo. Não processamos pedidos, não lidamos com pagamentos ou gerenciamos transações. Todas as informações fornecidas são para fins de pesquisa e educacionais.' },
        { h2: 'O que Fornecemos', content: 'Nosso hub de recursos inclui categorias de produtos organizadas, guias de compra, informações sobre custos de envio, dicas de controle de qualidade e ferramentas gerais de pesquisa. Nos esforçamos para manter nosso conteúdo atualizado e relevante, ajudando os usuários a navegar no processo de compra com mais confiança e melhores informações.' },
        { h2: 'Contato', content: 'Para perguntas ou sugestões sobre nosso conteúdo, entre em contato através dos nossos canais de comunicação. Valorizamos o feedback e buscamos continuamente melhorar nosso hub de recursos para a comunidade.' },
      ],
    },
    de: {
      title: 'Über uns | CSSBuy Spreadsheet Unabhängige Ressource',
      h1: 'Über CSSBuy Spreadsheet',
      desc: 'Erfahren Sie mehr über unseren unabhängigen CSSBuy Spreadsheet Ressourcen-Hub. Wir bieten organisierte Produktrecherche-Guides, Kategorien und Kaufressourcen.',
      sections: [
        { h2: 'Unsere Mission', content: 'CSSBuy Spreadsheet ist ein unabhängiger Ressourcen-Hub, der sich der Unterstützung von Käufern bei der Organisation ihrer Produktrecherche widmet. Wir glauben, dass strukturierte Informationen zu besseren Kaufentscheidungen führen. Unser Ziel ist es, klare, kategorisierte und zugängliche Ressourcen bereitzustellen, die den Rechercheprozess für alle vereinfachen, die daran interessiert sind, Produkte über Agenten zu kaufen.' },
        { h2: 'Unabhängige Ressource', content: 'Diese Website ist nicht mit CSSBuy verbunden, von CSSBuy unterstützt oder von CSSBuy betrieben. Wir sind eine unabhängige Drittanbieter-Ressource, die nur informative Inhalte bereitstellt. Wir bearbeiten keine Bestellungen, handhaben keine Zahlungen oder verwalten Transaktionen. Alle bereitgestellten Informationen dienen ausschließlich Recherche- und Bildungszwecken.' },
        { h2: 'Was wir bieten', content: 'Unser Ressourcen-Hub umfasst organisierte Produktkategorien, Kaufguides, Informationen zu Versandkosten, Qualitätskontroll-Tipps und allgemeine Recherche-Tools. Wir bemühen uns, unsere Inhalte aktuell und relevant zu halten, und helfen Nutzern, den Kaufprozess mit mehr Vertrauen und besseren Informationen zu navigieren.' },
        { h2: 'Kontakt', content: 'Für Fragen oder Anregungen zu unseren Inhalten wenden Sie sich bitte über unsere Kontaktkanäle an uns. Wir schätzen Feedback und streben danach, unseren Ressourcen-Hub kontinuierlich für die Community zu verbessern.' },
      ],
    },
  };

  const c = content[langCode] || content.en;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <header className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 lg:px-8 bg-background-100 border-b border-background-200">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {c.h1}
          </h1>
        </div>
      </header>

      <article className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <nav className="mb-8 text-sm text-foreground-500">
            <Link to={`/${langCode}/`} className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">About</span>
          </nav>

          {c.sections.map((section, idx) => (
            <div key={idx} className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {section.h2}
              </h2>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                {section.content}
              </p>
            </div>
          ))}

          <div className="mt-12 p-6 bg-primary-50 rounded-xl border border-primary-200">
            <Link to={`/${langCode}/`} className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Back to Home
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}